import { SortingState } from "@tanstack/react-table";
import { Constants } from "appConstants";
import { IcoEdit } from "assets/icons";
import { Button, Loading, Table } from "components";
import StatusTag from "components/StatusTag";
import { format, parseISO } from "date-fns";
import FileHelper from "helpers/FileHelper";
import TableHelper from "helpers/TableHelper";
import { useStandardToast } from "hooks";
import { useQuerystringParams } from "hooks/querystringFilter";
import { Term } from "models/term/Term";
import { TenantFilters } from "pages/backoffice/Tenant/List/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TermService } from "services/term";
import "./styles.scss";

export type Section = {
	[field: string]: any;
};

type TermsDetailsMainPanelProps = {
	activeTerm?: Term;
};

export default function TermsDetailsMainPanel({
	activeTerm
}: Readonly<TermsDetailsMainPanelProps>) {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [totalPages, setTotalPages] = useState<number>(1);
	const termService = new TermService();
	const { showErrorToast } = useStandardToast();
	const [termList, setTermList] = useState<Term[] | undefined>(undefined);
	const [filters, setFilters] = useState<any>({
		size: 10,
		page: 0,
		typeIds: activeTerm?.type.id
	});
	const [sorting, setSorting] = useState<SortingState>([]);
	const { updateUrl } = useQuerystringParams<TenantFilters>(
		setFilters,
		sorting,
		setSorting
	);

	const parseParams = () => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const filtersParams = {
			...filters,
			name: filters.search
		};
		if ("search" in filtersParams) {
			delete filtersParams.search;
		}
		return {
			...sortParam,
			...filtersParams
		};
	};

	function getTreatedStatus(termStatus: string | undefined) {
		return Constants.strings.pages.listing.term.statusType[
			termStatus?.toLowerCase() as keyof typeof Constants.strings.pages.listing.term.statusType
		];
	}

	function isScheduledTermStatus(termStatus: string | undefined): boolean {
		return (
			Constants.strings.pages.listing.term.statusType.scheduled === termStatus
		);
	}

	function extractFileName(filePath: string) {
		return filePath.split("/").pop();
	}

	function prepareResponse(content: Term[]) {
		return content.map((termObject) => {
			termObject.status = getTreatedStatus(termObject.status);
			termObject.fileName = extractFileName(termObject.pdfUrl);
			return termObject;
		});
	}

	function formatTermDate(date: string) {
		try {
			return format(parseISO(date), "dd/MM/yyyy");
		} catch (error) {
			return date;
		}
	}

	const termColumns: any[] = [
		{
			header: Constants.strings.pages.listing.term.headers.fileName,
			accessorKey: "fileName",
			accessorFn: (row: any) => row,
			cell: (row: any) => (
				<div className="flex gap-x-2 items-center">
					<a
						href={row.getValue().pdfUrl}
						className="underline"
						onClick={(e) => e.stopPropagation()}
						target="_blank"
						rel="noreferrer"
					>
						{FileHelper.getFileNameFromUrl(row.getValue().fileName)}
					</a>
					{isScheduledTermStatus(row.getValue().status) && (
						<div className="text-align--center align-items--center arrow-right">
							<Button
								kind="icon"
								size="small"
								styled="ghost"
								cssClass="bg-transparent"
								onClick={() =>
									navigate(`/backoffice/tenants/term/${row.getValue().id}`)
								}
							>
								<IcoEdit size="16" />
							</Button>
						</div>
					)}
				</div>
			)
		},
		{
			header: Constants.strings.pages.listing.term.headers.activationDate,
			accessorKey: "startDate",
			cell: (rowData: any) => <div>{formatTermDate(rowData.getValue())}</div>
		},
		{
			header: Constants.strings.pages.listing.term.headers.status,
			accessorKey: "status",
			cell: (rowData: any) => (
				<div className="text-align--center align-items--center">
					<StatusTag>{rowData.getValue()}</StatusTag>
				</div>
			),
			enableSorting: false
		}
	];

	const onPageChange = (selectedPage: number, pageSize: number) => {
		const newFilters = {
			...filters,
			size: pageSize,
			page: selectedPage - 1
		};
		setFilters(newFilters);
	};

	useEffect(() => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const newFilters = {
			...filters,
			...sortParam
		};
		updateUrl(newFilters);
	}, [sorting]);

	useEffect(() => {
		setIsLoading(true);
		termService
			.getList(parseParams())
			.then((termResponse: any) => {
				setTermList(prepareResponse(termResponse.content));
				setTotalPages(termResponse.totalPages);
			})
			.catch(() => {
				showErrorToast(Constants.strings.errors.failedToGetTermList);
			})
			.finally(() => setIsLoading(false));
	}, [filters, activeTerm]);

	return (
		<div className="terms-details-main-panel">
			{termList && (
				<div className="card">
					<div className="card-header-wrapper history-card-title">
						Histórico
						<Button
							kind="link"
							styled="ghost"
							size="small"
							cssClass="link-button font-normal"
							onClick={() =>
								navigate(`/backoffice/tenants/term/new/${activeTerm?.id}`)
							}
						>
							<div>Atualizar termo</div>
						</Button>
					</div>
					{!isLoading ? (
						<div className="history-block">
							<Table
								columns={termColumns}
								data={termList as Term[]}
								sorting={sorting}
								setSorting={setSorting}
								numberOfInstancesOptions={[10, 50, 100]}
								currentPage={filters.page + 1}
								pageSize={filters.size}
								totalPages={totalPages}
								onPageChange={onPageChange}
								cssCompact
							/>
						</div>
					) : (
						<div className="tenant-list-loading">
							<Loading type="primary" size={75} />
						</div>
					)}
				</div>
			)}
		</div>
	);
}
